/*************************************************************
 * Enhanced FilterPanel.jsx - Global filter controls with configuration integration
 *************************************************************/
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext.jsx';
import { api } from '../../api/reportingApi.js';

export const FilterPanel = ({ filters, onFiltersChange, showSortBy = false }) => {
    const { state } = useAppContext();
    const [availableOptions, setAvailableOptions] = useState({
        projects: [],
        stations: [],
        lotIds: [],
    });

    // Get configuration preferences
    const {
        default_date_range,
        includeManualImages
    } = state.preferences;

    // --- DEBOUNCE FIX: Local state for the confidence slider ---
    // This state updates instantly to provide a responsive UI.
    const [localConfidence, setLocalConfidence] = useState(filters.confidence || 0);

    // Fetch initial options for Parts and Stations
    useEffect(() => {
        const fetchInitialOptions = async () => {
            try {
                console.log('[FilterPanel] Fetching initial filter options...');
                
                const [projects, stations] = await Promise.all([
                    api.getProjects(),
                    api.getStations()
                ]);
                
                console.log('[FilterPanel] Fetched projects:', projects);
                console.log('[FilterPanel] Fetched stations:', stations);
                
                setAvailableOptions(prev => ({
                    ...prev,
                    projects: projects?.map(p => p.name) || [],
                    stations: stations || [],
                }));
            } catch (error) {
                console.error("[FilterPanel] Failed to fetch filter options:", error);
            }
        };
        fetchInitialOptions();
    }, []);

    // Fetch Lot IDs when a project is selected
    useEffect(() => {
        if (filters.project) {
            console.log(`[FilterPanel] Fetching lot IDs for project: ${filters.project}`);
            
            api.getLotIdentifiers(filters.project)
                .then(lotIds => {
                    console.log(`[FilterPanel] Fetched lot IDs for ${filters.project}:`, lotIds);
                    setAvailableOptions(prev => ({ ...prev, lotIds: lotIds || [] }));
                })
                .catch(err => {
                    console.error("[FilterPanel] Failed to fetch lot IDs:", err);
                    setAvailableOptions(prev => ({ ...prev, lotIds: [] }));
                });
        } else {
            setAvailableOptions(prev => ({ ...prev, lotIds: [] }));
        }
    }, [filters.project]);
    
    // --- DEBOUNCE FIX: useEffect to watch for changes in localConfidence ---
    // This effect will run 500ms after the user stops changing the slider.
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            // Only trigger the global filter update if the value has actually changed.
            if (filters.confidence !== localConfidence) {
                console.log(`[FilterPanel] Updating confidence filter from ${filters.confidence} to ${localConfidence}`);
                onFiltersChange({ ...filters, confidence: localConfidence });
            }
        }, 500); // 500ms delay

        // Cleanup function to clear the timer if the user starts sliding again
        return () => {
            clearTimeout(debounceTimer);
        };
    }, [localConfidence, filters.confidence, onFiltersChange]); // Re-run this effect whenever the local confidence changes

    // --- DEBOUNCE FIX: When global filters change, update the local slider value ---
    useEffect(() => {
        setLocalConfidence(filters.confidence || 0);
    }, [filters.confidence]);

    // Initialize default date range from configuration if not set
    useEffect(() => {
        if (!filters.dateRange && default_date_range) {
            console.log(`[FilterPanel] Setting default date range from config: ${default_date_range}`);
            onFiltersChange({ ...filters, dateRange: default_date_range });
        }
    }, [default_date_range, filters, onFiltersChange]);

    const handleFilterChange = (key, value) => {
        console.log(`[FilterPanel] Filter changed: ${key} = ${value}`);
        
        const newFilters = { ...filters, [key]: value };
        
        // Clear dependent filters when parent filter changes
        if (key === 'project') {
            newFilters.lotId = '';
            console.log('[FilterPanel] Cleared lotId due to project change');
        }
        
        onFiltersChange(newFilters);
    };

    const handleSortChange = (e) => {
        const value = e.target.value;
        let sortBy = 'inspection_time';
        let sortDirection = 'desc';

        if (value === 'confidence_asc') {
            sortBy = 'confidence';
            sortDirection = 'asc';
        } else if (value === 'confidence_desc') {
            sortBy = 'confidence';
            sortDirection = 'desc';
        }

        console.log(`[FilterPanel] Sort changed: ${sortBy} ${sortDirection}`);
        onFiltersChange({ ...filters, sortBy, sortDirection });
    };

    const getCurrentSortValue = () => {
        if (filters.sortBy === 'confidence') {
            return `confidence_${filters.sortDirection}`;
        }
        return 'inspection_time_desc';
    };

    const clearAllFilters = () => {
        console.log('[FilterPanel] Clearing all filters');
        
        // Reset to default configuration values
        const defaultFilters = {
            dateRange: default_date_range || 'week',
            project: '',
            station: '',
            lotId: '',
            defectType: '',
            modelAgreement: '',
            confidence: 0,
            sortBy: 'inspection_time',
            sortDirection: 'desc',
        };
        
        console.log('[FilterPanel] Reset filters to defaults:', defaultFilters);
        onFiltersChange(defaultFilters);
    };

    // Count active filters (excluding defaults)
    const getActiveFiltersCount = () => {
        const defaultValues = ['week', default_date_range, 'inspection_time', 'desc'];
        return Object.values(filters).filter(v =>
            v && v !== 0 && !defaultValues.includes(v)
        ).length;
    };

    const activeFiltersCount = getActiveFiltersCount();

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-filter text-primary me-2"></i>
                        Filters
                        {activeFiltersCount > 0 && (
                            <span className="badge bg-primary ms-2">
                                {activeFiltersCount}
                            </span>
                        )}
                    </h5>
                    {activeFiltersCount > 0 && (
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={clearAllFilters}
                            title="Reset filters to configuration defaults"
                        >
                            <i className="fas fa-times me-1"></i>
                            Clear All
                        </button>
                    )}
                </div>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-lg-3 col-md-6 mb-3">
                        <label className="form-label small fw-bold">Date Range</label>
                        <select
                            className="form-select form-select-sm"
                            value={filters.dateRange || default_date_range || 'week'}
                            onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                        >
                            <option value="today">Today</option>
                            <option value="week">Last 7 Days</option>
                            <option value="month">Last 30 Days</option>
                            <option value="year">Last Year</option>
                            <option value="all">All Time</option>
                        </select>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                        <label className="form-label small fw-bold">Part</label>
                        <select
                            className="form-select form-select-sm"
                            value={filters.project || ''}
                            onChange={(e) => handleFilterChange('project', e.target.value)}
                        >
                            <option value="">All Parts</option>
                            {availableOptions.projects.map(project => (
                                <option key={project} value={project}>{project}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                        <label className="form-label small fw-bold">Station</label>
                        <select
                            className="form-select form-select-sm"
                            value={filters.station || ''}
                            onChange={(e) => handleFilterChange('station', e.target.value)}
                        >
                            <option value="">All Stations</option>
                            {availableOptions.stations.map(station => (
                                <option key={station.station_id} value={station.station_id}>
                                    {station.station_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                        <label className="form-label small fw-bold">Lot ID</label>
                        <select
                            className="form-select form-select-sm"
                            value={filters.lotId || ''}
                            onChange={(e) => handleFilterChange('lotId', e.target.value)}
                            disabled={!filters.project || !availableOptions.lotIds || availableOptions.lotIds.length === 0}
                        >
                            <option value="">
                                {!filters.project ? 'Select Part First' : 
                                 availableOptions.lotIds.length === 0 ? 'No Lots Available' : 'All Lots'}
                            </option>
                            {availableOptions.lotIds.map(id => (
                                <option key={id} value={id}>{id}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label small fw-bold">
                            Minimum Confidence: {localConfidence}%
                        </label>
                        <input
                            type="range"
                            className="form-range"
                            min="0"
                            max="100"
                            value={localConfidence}
                            onChange={(e) => setLocalConfidence(parseInt(e.target.value))}
                        />
                        <div className="d-flex justify-content-between">
                            <small className="text-muted">0%</small>
                            <small className="text-muted">100%</small>
                        </div>
                    </div>

                    {showSortBy && (
                         <div className="col-lg-3 col-md-6 mb-3">
                            <label className="form-label small fw-bold">Sort By</label>
                            <select
                                className="form-select form-select-sm"
                                value={getCurrentSortValue()}
                                onChange={handleSortChange}
                            >
                                <option value="inspection_time_desc">Most Recent</option>
                                <option value="confidence_desc">Confidence (Descending)</option>
                                <option value="confidence_asc">Confidence (Ascending)</option>
                            </select>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};